import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { Sidebar } from './sidebar'
import { MobileHeader } from './mobile-header'
import { Toaster } from '@/components/ui/toaster'
import { useAuthStore } from '@/stores/auth-store'
import { authApi } from '@/lib/api'

export function Shell() {
  const clear = useAuthStore((s) => s.clear)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return
      }

      // Alt+Shift+Q → log out
      if (e.altKey && e.shiftKey && e.code === 'KeyQ') {
        e.preventDefault()
        authApi.logout().then(() => clear())
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [clear])

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background md:flex-row">
      {/* Desktop sidebar — hidden on mobile */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile sidebar drawer (Radix Dialog) */}
      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 md:hidden" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed left-0 top-0 z-40 h-full md:hidden"
          >
            <Dialog.Title className="sr-only">Menu</Dialog.Title>
            <Sidebar onClose={() => setMobileOpen(false)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Content column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header — visible only below md */}
        <MobileHeader onHamburger={() => setMobileOpen(true)} />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-6">
            <Outlet />
          </div>
        </main>
      </div>

      <Toaster />
    </div>
  )
}
